import { useCallback, useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { AtonChartData, parseAtonGraphData } from './parseGraphData';
import { useSession } from '@ais-ui/websockets';
import { useBasePath } from '@ais-ui/custom-hooks';
import { getBasePath } from '../getAISBasePath';
import {
  ParsedHostState,
  parseHostStatesFromHtml,
  transformHostWithState,
} from './parseHostStatesFromHtml';

/**
 * Configuration for the CheckMK API
 */
class Configuration {
  basePath: string;
  username?: string;
  password?: string;
  headers: Record<string, string>;

  constructor(options: {
    basePath: string;
    username?: string;
    password?: string;
    headers?: Record<string, string>;
  }) {
    this.basePath = options.basePath;
    this.username = options.username;
    this.password = options.password;
    this.headers = options.headers || {};
  }
}

/**
 * CheckMK API client
 */
class CheckMKApi {
  private configuration: Configuration;

  constructor(configuration: Configuration) {
    this.configuration = configuration;
  }

  private async request<T>(endpoint: string, options: any = {}): Promise<T> {
    const url = `${this.configuration.basePath}${endpoint}`;

    try {
      const axiosConfig: any = {
        url,
        method: options.method || 'GET',
        headers: {
          Accept: 'application/json',
          ...this.configuration.headers,
          ...options.headers,
        },
        data: options.body,
        withCredentials: false,
      };

      // Only add auth if we have both username and password
      if (this.configuration.username && this.configuration.password) {
        axiosConfig.auth = {
          username: this.configuration.username,
          password: this.configuration.password,
        };
      }

      const response = await axios(axiosConfig);
      return response.data;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  /**
   * Get all hosts from AtoN folder
   */
  async getAtonHosts(): Promise<CheckMKHostResponse> {
    return this.request<CheckMKHostResponse>(
      '/api/1.0/objects/folder_config/~AtoN/collections/hosts'
    );
  }

  /**
   * Get all hosts configuration
   */
  async getAllHosts(): Promise<CheckMKHostResponse> {
    return this.request<CheckMKHostResponse>(
      '/api/1.0/domain-types/host_config/collections/all'
    );
  }

  async getHostDetails(hostName: string): Promise<any> {
    return this.request(`/api/1.0/objects/host_config/${hostName}`);
  }

  async getHostServices(hostName: string): Promise<ServiceDiscoveryResponse> {
    return this.request(`/api/1.0/objects/service_discovery/${hostName}`);
  }

  async getSimplifiedHostServices(
    hostName: string
  ): Promise<Record<string, CheckTableService>> {
    const response = await this.getHostServices(hostName);
    const checkTable: Record<string, CheckTableService> =
      response.extensions.check_table;
    const transformedServices: Record<string, CheckTableService> = {};

    Object.values(checkTable).forEach((service) => {
      transformedServices[service.id] = service;
    });
    return transformedServices;
  }

  async getHostServicesWithValues(hostName: string): Promise<any> {
    const queryParams = new URLSearchParams();

    // Add columns we want for live monitoring data
    queryParams.append('columns', 'host_name');
    queryParams.append('columns', 'description');
    queryParams.append('columns', 'state');
    queryParams.append('columns', 'plugin_output');
    queryParams.append('columns', 'perf_data');
    queryParams.append('columns', 'last_check');
    queryParams.append('columns', 'check_command');

    // Filter by specific host
    const query = {
      op: '=',
      left: 'host_name',
      right: hostName,
    };
    queryParams.append('query', JSON.stringify(query));

    return this.request(
      `/api/1.0/domain-types/service/collections/all?${queryParams.toString()}`
    );
  }

  async getServiceGraphPopup(
    hostName: string,
    serviceName: string
  ): Promise<AtonChartData | null> {
    const params = new URLSearchParams({
      host_name: hostName,
      service: serviceName,
      _ajaxid: '0',
    });

    try {
      const htmlResponse = await this.request(
        `/host_service_graph_popup.py?${params.toString()}`
      );

      // Check if response contains error messages
      if (typeof htmlResponse === 'string') {
        if (
          htmlResponse.includes('Cannot calculate graph recipes') ||
          htmlResponse.includes('Cannot fetch data via Livestatus') ||
          htmlResponse.includes('graph brokengraph')
        ) {
          console.warn(
            `No graph data available for ${hostName}/${serviceName}:`,
            htmlResponse
          );
          return null;
        }
      }

      return parseAtonGraphData(htmlResponse as string);
    } catch (error) {
      console.error('Error getting service graph data:', error);
      throw error;
    }
  }

  /**
   * Get AtoN host states from the hosts view
   */
  async getAtonHostStates(): Promise<ParsedHostState[]> {
    try {
      const htmlResponse = await this.request('/view.py?view_name=allhosts');
      return parseHostStatesFromHtml(htmlResponse as string);
    } catch (error) {
      console.error('Error getting AtoN host states:', error);
      throw error;
    }
  }
}

/**
 * CheckMK API Response Types
 */
export interface CheckMKLink {
  domainType: string;
  rel: string;
  href: string;
  method: string;
  type: string;
}

export interface HostMetaData {
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface HostAttributes {
  ipaddress: string;
  meta_data: HostMetaData;
  tag_site_type?: string;
  tag_agent: string;
  tag_address_family: string;
  [key: string]: any; // For additional dynamic attributes
}

export interface HostExtensions {
  folder: string;
  attributes: HostAttributes;
  effective_attributes: any | null;
  is_cluster: boolean;
  is_offline: boolean;
  cluster_nodes: any | null;
}

export interface CheckMKHost {
  links: CheckMKLink[];
  domainType: string;
  id: string;
  title: string;
  members: Record<string, any>;
  extensions: HostExtensions;
}

export interface CheckMKHostResponse {
  links: CheckMKLink[];
  id: string;
  domainType: string;
  value: CheckMKHost[];
}

export interface ServiceLink {
  rel: string;
  href: string;
  method: string;
  type: string;
  domainType: string;
  body_params: {
    target_phase: string;
    check_type: string;
    service_item: string;
  };
  title: string;
}

export interface ServiceExtensions {
  host_name: string;
  check_plugin_name: string;
  service_name: string;
  service_item: string;
  service_phase: string;
}

export interface CheckTableService {
  id: string;
  memberType: string;
  value: string;
  format: string;
  title: string;
  links: ServiceLink[];
  extensions: ServiceExtensions;
}

export interface HostLabel {
  value: string;
  plugin_name: string;
}

export interface ServiceDiscoveryExtensions {
  check_table: Record<string, CheckTableService>;
  host_labels: Record<string, HostLabel>;
  vanished_labels: Record<string, any>;
  changed_labels: Record<string, any>;
}

export interface ServiceDiscoveryLink {
  domainType: string;
  rel: string;
  href: string;
  method: string;
  type: string;
}

export interface ServiceDiscoveryResponse {
  links: ServiceDiscoveryLink[];
  domainType: string;
  id: string;
  title: string;
  members: Record<string, any>;
  extensions: ServiceDiscoveryExtensions;
}

/**
 * Simplified host type for application use
 */
export interface AtonHost {
  id: string;
  title: string;
  ipAddress: string;
  folder: string;
  siteType?: string;
  agent: string;
  addressFamily: string;
  isCluster: boolean;
  isOffline: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  hostState: 'UP' | 'DOWN' | 'UNREACHABLE' | 'PENDING';
  serviceStates: {
    ok: number;
    warning: number;
    unknown: number;
    critical: number;
    pending: number;
  };
}

/**
 * Transform CheckMK host to simplified host format
 */
export const transformHost = (checkMKHost: CheckMKHost): AtonHost => {
  return {
    id: checkMKHost.id,
    title: checkMKHost.title,
    ipAddress: checkMKHost.extensions.attributes.ipaddress,
    folder: checkMKHost.extensions.folder,
    siteType: checkMKHost.extensions.attributes.tag_site_type,
    agent: checkMKHost.extensions.attributes.tag_agent,
    addressFamily: checkMKHost.extensions.attributes.tag_address_family,
    isCluster: checkMKHost.extensions.is_cluster,
    isOffline: checkMKHost.extensions.is_offline,
    createdAt: checkMKHost.extensions.attributes.meta_data.created_at,
    updatedAt: checkMKHost.extensions.attributes.meta_data.updated_at,
    createdBy: checkMKHost.extensions.attributes.meta_data.created_by,
    hostState: checkMKHost.extensions.is_offline ? 'DOWN' : 'UP',
    serviceStates: {
      ok: 0,
      warning: 0,
      unknown: 0,
      critical: 0,
      pending: 0,
    },
  };
};

/**
 * Hook to get CheckMK API instance
 */
/**
 * Hook to get CheckMK API instance
 */
const useCheckMKApi = () => {
  const username = 'cmkadmin';
  const password = 'cmk';

  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  const basePath = getBasePath();

  const configuration = useMemo(() => {
    // if (hostname.includes('localhost')) {
    //   const authHeader = getAuthHeaderFromCredentials(username, password);
    //   return new Configuration({
    //     basePath: `${basePath}/r138_5_daily/check_mk`,
    //     username,
    //     password,
    //     headers: {
    //       'X-Application-Name': 'marinepulse',
    //       Authorization: authHeader,
    //     },
    //   });
    // } else {
    // For AISWeb token, don't pass username/password
    return new Configuration({
      basePath: `${basePath}/check_mk`,
      headers: {
        'Aisweb-Session': sessionId || '',
        'X-Application-Name': 'marinepulse',
      },
    });
    // }
  }, [hostname, basePath, sessionId]);

  const checkMKApi = useMemo(
    () => new CheckMKApi(configuration),
    [configuration]
  );

  return checkMKApi;
};

/**
 * Hook to fetch AtoN hosts from CheckMK
 */
export const useFetchAtonHosts = (): UseQueryResult<AtonHost[]> => {
  const checkMKApi = useCheckMKApi();

  return useQuery(
    ['FetchAtonHosts'],
    async () => {
      try {
        const result = await checkMKApi.getAtonHosts();
        // Transform the raw CheckMK response to simplified Host objects
        return result.value.map(transformHost);
      } catch (error) {
        console.error('Error fetching AtoN hosts:', error);
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};

const ALLOWED_HOSTNAMES = [
  'mdmtest.gatehouse.local',
  'localhost',
  'mdmdemo.gatehousemaritime.com',
];

export const useFetchAtonHostsWithState = (): UseQueryResult<AtonHost[]> => {
  const checkMKApi = useCheckMKApi();
  const { hostname } = useBasePath();

  return useQuery(
    ['FetchAtonHostsWithState'],
    async () => {
      try {
        // Fetch both config data and state data in parallel
        const [configResponse, stateData] = await Promise.all([
          checkMKApi.getAtonHosts(),
          checkMKApi.getAtonHostStates(),
        ]);

        // Create a map for quick lookup
        const stateMap = new Map<string, ParsedHostState>();
        stateData.forEach((state) => {
          stateMap.set(state.hostName, state);
        });

        // Transform hosts with state data
        return configResponse.value.map((host) => {
          const stateInfo = stateMap.get(host.id);
          return transformHostWithState(host, stateInfo);
        });
      } catch (error) {
        console.error('Error fetching AtoN hosts with state:', error);
        throw error;
      }
    },
    {
      // Only enable the query if the current hostname is in the allowed list, this should ideally be controlled by the server
      //  but for the MVP we just handle it like this
      enabled: ALLOWED_HOSTNAMES.includes(hostname),
      retry: 1,
      refetchOnWindowFocus: false,
      // Refresh every 30 seconds to get updated state information
      refetchInterval: 30000,
    }
  );
};

export const useFetchAtonDetails = () => {
  const checkMKApi = useCheckMKApi();

  const fetchAtonDetails = useCallback(
    async (hostName: string) => {
      return await checkMKApi.getHostServicesWithValues(hostName);
    },
    [checkMKApi]
  );

  return fetchAtonDetails;
};

export const useFetchAtonDetailsQuery = (
  hostName: string
): UseQueryResult<any, Error> => {
  const checkMKApi = useCheckMKApi();

  return useQuery(['aton-details', hostName], async () => {
    if (!hostName) {
      throw new Error('Host name is required');
    }
    return await checkMKApi.getHostServicesWithValues(hostName);
  });
};

export const useFetchServiceGraph = () => {
  const checkMKApi = useCheckMKApi();

  const fetchServiceGraph = useCallback(
    async (hostName: string, serviceName: string) => {
      return await checkMKApi.getServiceGraphPopup(hostName, serviceName);
    },
    [checkMKApi]
  );

  return fetchServiceGraph;
};

export const useFetchAtonServices = (
  hostName: string | undefined
): UseQueryResult<any, Error> => {
  const checkMKApi = useCheckMKApi();

  return useQuery(
    ['aton-services', hostName],
    async () => {
      if (!hostName) {
        throw new Error('Host name is required');
      }

      return await checkMKApi.getSimplifiedHostServices(hostName);
    },
    {
      enabled: !!hostName,
    }
  );
};
